/**
 * @file voipPendingAnswer.js
 * @description
 * Module-level (React-free) state for the KILLED-app incoming VoIP call path.
 *
 * Problem: when the app is killed, a wake push rings the native call UI, but the
 * jssip UA has not registered yet and the SIP INVITE has not arrived. If the user
 * taps Answer first, there is no SIP session to answer — the intent must be
 * latched and applied when the INVITE lands. This module holds that latch, the
 * push caller identity (for matching), and an early-start registry so the wake
 * push can kick off SIP registration without waiting for any UI.
 *
 * Everything here lives at module scope on purpose: it must survive the window
 * before CallProvider mounts (cold start) and work in the Android headless
 * FCM task (no React tree).
 *
 * Android headless note: with no React tree there is no CallProvider, so
 * `requestUaStart()` only raises a flag. Registration then begins when the
 * activity launches on Answer (index.js answer handler calls
 * `bringAppToForeground()` → RNCallKeep.backToForeground()), CallProvider mounts,
 * registers its starter and consumes the flag + the pending answer.
 *
 * Pure JavaScript (ESM).
 */

/** How long an answered-but-uninvited call may wait for its SIP INVITE. */
export const PENDING_ANSWER_WINDOW_MS = 20000;

let pending = null; // {callId, at}
let expireTimer = null;
let pushCaller = null; // {callId, from, contactName, suggestedName}
let uaStarter = null;
let startRequested = false;

/** Keep only digits, for tolerant number comparison. */
function digits(value) {
    return String(value || '').replace(/\D+/g, '');
}

/**
 * Remember who the wake push said is calling (set by displayIncomingCallFromPush).
 * @param {{callId?: string, from?: string, contactName?: string, suggestedName?: string}} data Push payload.
 * @returns {void}
 */
export function setPushCaller(data) {
    pushCaller = data && (data.callId || data.from)
        ? {callId: data.callId ? String(data.callId) : '', from: data.from ? String(data.from) : '', contactName: data.contactName || '', suggestedName: data.suggestedName || ''}
        : null;
}

/**
 * @returns {({callId: string, from: string, contactName: string, suggestedName: string}|null)} Last push caller.
 */
export function getPushCaller() {
    return pushCaller;
}

/**
 * Whether a SIP remote number plausibly is the push caller. Unknown push caller
 * or unknown remote number matches (we cannot rule it out). Compares digit
 * suffixes so +36301234567 / 06301234567 / 301234567 are equivalent.
 * @param {string} remote SIP remote identity user part.
 * @param {{from?: string}|null} [caller] Push caller; defaults to the stored one.
 * @returns {boolean}
 */
export function matchesPushCaller(remote, caller = pushCaller) {
    const a = digits(remote);
    const b = digits(caller && caller.from);
    if (!a || !b) {
        return true;
    }
    const n = Math.min(a.length, b.length, 9);

    return a.slice(-n) === b.slice(-n);
}

/**
 * Pick the session to auto-answer: prefer the one whose number matches the push
 * caller, else the first.
 * @template T
 * @param {Array<{from: string} & T>} candidates Incoming session candidates.
 * @param {{from?: string}|null} [caller] Push caller; defaults to the stored one.
 * @returns {(T|null)}
 */
export function pickMatchingSession(candidates, caller = pushCaller) {
    if (!Array.isArray(candidates) || candidates.length === 0) {
        return null;
    }
    const hasCaller = digits(caller && caller.from).length > 0;
    const hit = hasCaller ? candidates.find((c) => digits(c.from) && matchesPushCaller(c.from, caller)) : null;

    return hit || candidates[0];
}

/**
 * Record an Answer tap that has no SIP session yet. Idempotent per callId (the
 * original timestamp is kept so the window is not extended by repeats).
 * @param {{callId?: string}} [info] Native answer info.
 * @param {number} [now] Timestamp (ms).
 * @returns {{callId: string, at: number}}
 */
export function recordPendingAnswer(info, now = Date.now()) {
    const callId = info && info.callId ? String(info.callId) : '';
    if (pending && pending.callId === callId) {
        return pending;
    }
    pending = {callId, at: now};

    return pending;
}

/**
 * Read the latch. An expired entry is cleared and reported via `expired`.
 * @param {number} [now] Timestamp (ms).
 * @returns {{pending: ({callId: string, at: number}|null), expired: ({callId: string, at: number}|null)}}
 */
export function readPendingAnswer(now = Date.now()) {
    if (pending && now - pending.at > PENDING_ANSWER_WINDOW_MS) {
        const expired = pending;
        clearPendingAnswer();

        return {pending: null, expired};
    }

    return {pending, expired: null};
}

/**
 * Arm the expiry timer for the current latch (replaces any previous timer).
 * @param {function({callId: string, at: number}): void} onExpire Called once on timeout (e.g. end the native call).
 * @param {number} [now] Timestamp (ms).
 * @returns {boolean} Whether a timer was armed.
 */
export function armPendingExpiry(onExpire, now = Date.now()) {
    if (!pending) {
        return false;
    }
    if (expireTimer) {
        globalThis.clearTimeout(expireTimer);
    }
    const latched = pending;
    const remaining = Math.max(0, PENDING_ANSWER_WINDOW_MS - (now - latched.at));
    expireTimer = globalThis.setTimeout(() => {
        expireTimer = null;
        if (pending === latched) {
            pending = null;
            onExpire && onExpire(latched);
        }
    }, remaining);

    return true;
}

/**
 * Clear the latch + its timer (INVITE consumed, call ended, or timed out).
 * @returns {void}
 */
export function clearPendingAnswer() {
    pending = null;
    if (expireTimer) {
        globalThis.clearTimeout(expireTimer);
        expireTimer = null;
    }
}

/**
 * Register the function that starts SIP registration (CallProvider's `ensureUa`).
 * If a start was requested before it mounted, it fires immediately.
 * @param {function(): (Promise<any>|any)} starter Starter.
 * @returns {function(): void} Unregister.
 */
export function registerUaStarter(starter) {
    uaStarter = starter;
    if (startRequested) {
        startRequested = false;
        try {
            const r = starter();
            r && r.catch && r.catch(() => {});
        } catch (_error) { /* noop */ }
    }

    return () => {
        if (uaStarter === starter) {
            uaStarter = null;
        }
    };
}

/**
 * Ask for SIP registration NOW (called from the wake push). Runs the registered
 * starter, or remembers the request for CallProvider's mount.
 * @returns {boolean} True if a starter ran, false if the request was deferred.
 */
export function requestUaStart() {
    if (uaStarter) {
        try {
            const r = uaStarter();
            r && r.catch && r.catch(() => {});
        } catch (_error) { /* noop */ }

        return true;
    }
    startRequested = true;

    return false;
}

/** @returns {boolean} Whether a start request is waiting for a starter. */
export function hasDeferredUaStart() {
    return startRequested;
}

/**
 * Reset everything (tests only).
 * @returns {void}
 */
export function resetVoipPendingAnswer() {
    clearPendingAnswer();
    pushCaller = null;
    uaStarter = null;
    startRequested = false;
}
